/**
 * 登录验证码组件：根据后端 /auth/login/config 决定加载哪种验证码：
 *   - none      : 不渲染
 *   - image     : 图形验证码（与现有注册一致）
 *   - turnstile : Cloudflare Turnstile（动态注入官方 JS）
 *   - geetest   : 极验 V4（动态注入 gt4.js）
 *
 * 使用：
 *   const captchaRef = useRef<LoginCaptchaHandle>(null);
 *   <LoginCaptcha ref={captchaRef} />
 *   const payload = await captchaRef.current?.collect();
 */
import { useEffect, useImperativeHandle, useRef, useState, forwardRef } from "react";
import { Auth } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export type LoginCaptchaPayload = {
  captcha_id?: string; captcha_code?: string;
  turnstile_token?: string;
  geetest?: { lot_number: string; captcha_output: string; pass_token: string; gen_time: string };
};

export type LoginCaptchaHandle = {
  /** 收集验证码字段；若未通过返回 null */
  collect: () => Promise<LoginCaptchaPayload | null>;
  /** 重置验证码（登录失败后调用） */
  reset: () => void;
};

declare global {
  interface Window {
    turnstile?: any;
    initGeetest4?: (config: any, cb: (obj: any) => void) => void;
  }
}

function loadScript(src: string, id: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (document.getElementById(id)) return resolve();
    const s = document.createElement("script");
    s.src = src; s.async = true; s.defer = true; s.id = id;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("script load failed: " + src));
    document.head.appendChild(s);
  });
}

export const LoginCaptcha = forwardRef<LoginCaptchaHandle, { scope?: "login" | "register" }>(({ scope = "login" }, ref) => {
  const [cfg, setCfg] = useState<{ captcha: string; site_key?: string; captcha_id?: string } | null>(null);
  const [loadErr, setLoadErr] = useState<string>("");
  // image mode
  const [img, setImg] = useState<{ id: string; image: string } | null>(null);
  const [imgCode, setImgCode] = useState("");
  // turnstile
  const turnstileRef = useRef<HTMLDivElement | null>(null);
  const turnstileWidgetId = useRef<string | null>(null);
  const turnstileToken = useRef<string>("");
  const [turnstileReady, setTurnstileReady] = useState(false);
  // geetest
  const geetestObj = useRef<any>(null);
  const [geetestReady, setGeetestReady] = useState(false);

  const refreshImage = () => {
    Auth.captcha().then(setImg).catch(() => setImg(null));
  };

  useEffect(() => {
    const load = scope === "register" ? Auth.registerConfig : Auth.loginConfig;
    load().then((c: any) => setCfg(c)).catch(() => setCfg({ captcha: "none" }));
  }, [scope]);

  // Image
  useEffect(() => {
    if (cfg?.captcha === "image") refreshImage();
  }, [cfg?.captcha]);

  // Turnstile：等待 ref 挂载后再 render
  const turnstileMount = (el: HTMLDivElement | null) => {
    turnstileRef.current = el;
    if (!el || cfg?.captcha !== "turnstile" || !cfg.site_key) return;
    if (turnstileWidgetId.current) return; // 防止 StrictMode 重复挂载
    setLoadErr("");
    loadScript("https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit", "ztb-turnstile-js")
      .then(() => waitFor(() => !!window.turnstile, 5000))
      .then(() => {
        if (!turnstileRef.current || turnstileWidgetId.current) return;
        try {
          turnstileWidgetId.current = window.turnstile.render(turnstileRef.current, {
            sitekey: cfg.site_key,
            theme: "light",
            callback: (tok: string) => { turnstileToken.current = tok; },
            "error-callback": () => { turnstileToken.current = ""; },
            "expired-callback": () => { turnstileToken.current = ""; },
          });
          setTurnstileReady(true);
        } catch (e: any) {
          setLoadErr(`Turnstile 渲染失败：${e?.message || e}`);
        }
      })
      .catch((e) => setLoadErr(`Turnstile 加载失败：${e?.message || e}`));
  };

  // GeeTest
  useEffect(() => {
    if (cfg?.captcha !== "geetest" || !cfg.captcha_id) return;
    if (geetestObj.current) return;
    setLoadErr("");
    loadScript("https://static.geetest.com/v4/gt4.js", "ztb-geetest-js")
      .then(() => waitFor(() => !!window.initGeetest4, 5000))
      .then(() => {
        window.initGeetest4!({
          captchaId: cfg.captcha_id,
          product: "bind",
          language: "zho",
        }, (obj: any) => {
          geetestObj.current = obj;
          const host = document.getElementById("ztb-geetest-host");
          if (host) obj.appendTo("#ztb-geetest-host");
          obj.onReady?.(() => setGeetestReady(true));
          obj.onSuccess?.(() => setGeetestReady(true));
        });
      })
      .catch((e) => setLoadErr(`极验加载失败：${e?.message || e}`));
  }, [cfg?.captcha, cfg?.captcha_id]);

  useImperativeHandle(ref, () => ({
    async collect() {
      if (!cfg || cfg.captcha === "none") return {};
      if (cfg.captcha === "image") {
        if (!img?.id || !imgCode.trim()) return null;
        return { captcha_id: img.id, captcha_code: imgCode.trim() };
      }
      if (cfg.captcha === "turnstile") {
        if (!turnstileToken.current) return null;
        return { turnstile_token: turnstileToken.current };
      }
      if (cfg.captcha === "geetest") {
        const obj = geetestObj.current;
        if (!obj) return null;
        const r = obj.getValidate?.();
        if (!r) return null;
        return {
          geetest: {
            lot_number: r.lot_number,
            captcha_output: r.captcha_output,
            pass_token: r.pass_token,
            gen_time: r.gen_time,
          },
        };
      }
      return {};
    },
    reset() {
      if (cfg?.captcha === "image") { setImgCode(""); refreshImage(); }
      if (cfg?.captcha === "turnstile" && turnstileWidgetId.current) {
        window.turnstile?.reset(turnstileWidgetId.current);
        turnstileToken.current = "";
      }
      if (cfg?.captcha === "geetest") {
        geetestObj.current?.reset?.();
      }
    },
  }), [cfg, img, imgCode]);

  if (!cfg || cfg.captcha === "none") return null;

  if (cfg.captcha === "image") {
    return img ? (
      <div className="space-y-1.5">
        <Label>验证码</Label>
        <div className="flex gap-2">
          <Input required value={imgCode} onChange={(e) => setImgCode(e.target.value)} placeholder="请输入图中字符" />
          <button type="button" onClick={refreshImage} className="shrink-0" title="点击刷新">
            <img src={img.image} alt="captcha" className="h-10 rounded-md border" />
          </button>
        </div>
      </div>
    ) : <p className="text-sm text-destructive">验证码加载失败</p>;
  }

  if (cfg.captcha === "turnstile") {
    if (!cfg.site_key) {
      return <p className="text-sm text-destructive">未配置 Turnstile site_key，请联系管理员到「站点设置 → captcha.turnstile」补全。</p>;
    }
    return (
      <div className="space-y-1">
        <div ref={turnstileMount} />
        {!turnstileReady && !loadErr && <p className="text-xs text-muted-foreground">人机验证加载中...</p>}
        {loadErr && <p className="text-xs text-destructive">{loadErr}</p>}
      </div>
    );
  }

  if (cfg.captcha === "geetest") {
    if (!cfg.captcha_id) {
      return <p className="text-sm text-destructive">未配置极验 captcha_id，请联系管理员到「站点设置 → captcha.geetest」补全。</p>;
    }
    return (
      <div className="space-y-1">
        <div id="ztb-geetest-host" />
        {!geetestReady && !loadErr && <p className="text-xs text-muted-foreground">极验加载中...</p>}
        {loadErr && <p className="text-xs text-destructive">{loadErr}</p>}
      </div>
    );
  }
  return null;
});
LoginCaptcha.displayName = "LoginCaptcha";

/** 轮询等待某条件成立（适用于等待第三方脚本注入全局对象） */
function waitFor(cond: () => boolean, timeoutMs: number): Promise<void> {
  return new Promise((resolve, reject) => {
    const start = Date.now();
    const tick = () => {
      if (cond()) return resolve();
      if (Date.now() - start > timeoutMs) return reject(new Error("timeout"));
      setTimeout(tick, 50);
    };
    tick();
  });
}
